// Authentication Manager - Multiple Auth Methods
import CryptoJS from 'crypto-js';

const AUTH_TOKEN_KEY = 'auth_token';
const USER_DATA_KEY = 'encrypted_user_data';
const AUTH_METHOD_KEY = 'auth_method';
const NFC_CREDENTIAL_KEY = 'nfc_credential';

class AuthManager {
  constructor() {
    this.isAuthenticated = false;
    this.currentUser = null;
    this.authMethod = null;
  }

  // Check if user is currently authenticated
  isLoggedIn() {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp > Date.now() / 1000;
      } catch {
        return false;
      }
    }
    return false;
  }

  // Get current authentication method
  getAuthMethod() {
    return localStorage.getItem(AUTH_METHOD_KEY) || null;
  }

  // Get the raw JWT (sent to authenticated API endpoints so the server can verify
  // who is making the request instead of trusting an email in the request body).
  getToken() {
    return localStorage.getItem(AUTH_TOKEN_KEY) || '';
  }

  // Generate JWT token
  generateToken(userData) {
    const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
    const payload = btoa(JSON.stringify({
      id: userData.id,
      email: userData.email,
      exp: Math.floor(Date.now() / 1000) + (7 * 24 * 60 * 60), // 7 days
      iat: Math.floor(Date.now() / 1000)
    }));

    // Simple signature (in production, use proper JWT library)
    const signature = btoa('travel_app_signature');
    return `${header}.${payload}.${signature}`;
  }

  // Encrypt user data
  encryptData(data, password) {
    return CryptoJS.AES.encrypt(JSON.stringify(data), password).toString();
  }

  // Decrypt user data
  decryptData(encryptedData, password) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, password);
      return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch {
      return null;
    }
  }

  // Traditional email/password login
  async loginWithPassword(email, password) {
    try {
      console.log('[Auth] Attempting password login for:', email);

      const response = await fetch('./api/login.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
      });

      // Handle server errors that return HTML instead of JSON (e.g. Cloudflare 524 timeout)
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        if (response.status === 524) {
          throw new Error('Server timeout — please try again in a moment');
        }
        throw new Error(`Server error (${response.status}) — please try again later`);
      }

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Login failed');
      }

      // Store token and user data
      localStorage.setItem(AUTH_TOKEN_KEY, result.token);
      localStorage.setItem(AUTH_METHOD_KEY, 'password');
      localStorage.setItem('user_profile', JSON.stringify(result.user));

      this.isAuthenticated = true;
      this.currentUser = result.user;
      this.authMethod = 'password';

      console.log('✅ Password authentication successful');
      return { success: true, user: result.user };

    } catch (error) {
      console.error('❌ Password authentication failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Email registration is now handled server-side
  // These methods are deprecated and will be removed

  // Send welcome email via PHP backend
  async sendWelcomeEmail(userData) {
    try {
      console.log('📧 [Email] Sending welcome email via PHP backend to:', userData.email);

      const emailData = {
        email: userData.email,
        name: userData.name,
        nationality: userData.nationality || '',
        destination: userData.destination || 'your destination',
        acceptNews: userData.acceptNews || false
      };

      // Call PHP backend to send actual email
      console.log('🔍 [Email] Making request to:', '/api/send-welcome-email.php');
      console.log('🔍 [Email] Request data:', emailData);

      const response = await fetch('./api/send-welcome-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData)
      });

      console.log('🔍 [Email] Response status:', response.status);
      console.log('🔍 [Email] Response ok:', response.ok);

      // Handle server errors that return HTML instead of JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(`Server error (${response.status}) — email service unavailable`);
      }

      const result = await response.json();
      console.log('🔍 [Email] Response data:', result);

      if (!response.ok) {
        throw new Error(result.error || `HTTP ${response.status}: Failed to send email`);
      }

      console.log('✅ [Email] Welcome email sent successfully:', result);
      return { success: true, data: result };

    } catch (error) {
      console.error('❌ [Email] Failed to send welcome email:', error);
      return { success: false, error: error.message };
    }
  }

  // Register new user
  async register(email, password, name, additionalData = {}) {
    try {
      console.log('[Auth] Registering new user:', email);

      const response = await fetch('./api/register.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          name,
          nationality: additionalData.nationality || '',
          acceptNews: additionalData.acceptNews || false
        })
      });

      // Handle server errors that return HTML instead of JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        if (response.status === 524) {
          throw new Error('Server timeout — please try again in a moment');
        }
        throw new Error(`Server error (${response.status}) — please try again later`);
      }

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Registration failed');
      }

      // Store token and user data
      localStorage.setItem(AUTH_TOKEN_KEY, result.token);
      localStorage.setItem(AUTH_METHOD_KEY, 'password');
      localStorage.setItem('user_profile', JSON.stringify(result.user));

      this.isAuthenticated = true;
      this.currentUser = result.user;

      console.log('✅ User registration successful');
      console.log('📧 Welcome emails will be handled by Odoo integration');

      return { success: true, user: result.user };

    } catch (error) {
      console.error('❌ Registration failed:', error);
      return { success: false, error: error.message };
    }
  }

  // NFC Authentication
  async loginWithNFC() {
    try {
      if (!('NDEFReader' in window)) {
        throw new Error('NFC not supported on this device');
      }

      console.log('[Auth] Attempting NFC authentication...');

      const ndef = new NDEFReader();
      await ndef.scan();

      console.log('📱 NFC scan started - tap your NFC device');

      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('NFC scan timeout'));
        }, 30000);

        ndef.addEventListener('readingerror', () => {
          clearTimeout(timeout);
          reject(new Error('NFC read error'));
        });

        ndef.addEventListener('reading', ({ message, serialNumber }) => {
          clearTimeout(timeout);

          console.log('📡 NFC tag detected:', serialNumber);
          console.log('📄 NFC message:', message);

          // METHOD 1: Use serial number as key (current - works with any NFC tag)
          const storedCredential = localStorage.getItem(NFC_CREDENTIAL_KEY);

          if (storedCredential && storedCredential === serialNumber) {
            const userData = {
              id: Date.now(),
              nfcId: serialNumber,
              name: 'NFC User',
              authMethod: 'nfc'
            };

            const token = this.generateToken(userData);

            localStorage.setItem(AUTH_TOKEN_KEY, token);
            localStorage.setItem(AUTH_METHOD_KEY, 'nfc');

            this.isAuthenticated = true;
            this.currentUser = userData;

            console.log('✅ NFC authentication successful');
            resolve({ success: true, user: userData });
          } else {
            reject(new Error('NFC tag not recognized'));
          }
        });
      });

    } catch (error) {
      console.error('❌ NFC authentication failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Register NFC tag
  async registerNFC() {
    try {
      if (!('NDEFReader' in window)) {
        throw new Error('NFC not supported on this device');
      }

      console.log('[Auth] Registering NFC tag...');

      const ndef = new NDEFReader();
      await ndef.scan();

      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('NFC registration timeout'));
        }, 30000);

        ndef.addEventListener('reading', ({ serialNumber }) => {
          clearTimeout(timeout);

          console.log('📡 NFC tag registered:', serialNumber);
          localStorage.setItem(NFC_CREDENTIAL_KEY, serialNumber);

          resolve({ success: true, nfcId: serialNumber });
        });
      });

    } catch (error) {
      console.error('❌ NFC registration failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Biometric Authentication (WebAuthn)
  async loginWithBiometric() {
    try {
      if (!window.PublicKeyCredential) {
        throw new Error('WebAuthn not supported on this device');
      }

      console.log('[Auth] Attempting biometric authentication...');

      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: new Uint8Array(32),
          rp: { name: 'DIAS Travel Companion' },
          user: {
            id: new TextEncoder().encode('user123'),
            name: 'travel-user',
            displayName: 'Travel User'
          },
          pubKeyCredParams: [{ alg: -7, type: 'public-key' }],
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            userVerification: 'required'
          }
        }
      });

      const userData = {
        id: Date.now(),
        biometricId: credential.id,
        name: 'Biometric User',
        authMethod: 'biometric'
      };

      const token = this.generateToken(userData);

      localStorage.setItem(AUTH_TOKEN_KEY, token);
      localStorage.setItem(AUTH_METHOD_KEY, 'biometric');

      this.isAuthenticated = true;
      this.currentUser = userData;

      console.log('✅ Biometric authentication successful');
      return { success: true, user: userData };

    } catch (error) {
      console.error('❌ Biometric authentication failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Pin Code Authentication
  async loginWithPin(pin) {
    try {
      const storedPin = localStorage.getItem('user_pin');

      if (!storedPin) {
        throw new Error('No PIN set');
      }

      const hashedPin = CryptoJS.SHA256(pin).toString();

      if (hashedPin === storedPin) {
        const userData = {
          id: Date.now(),
          pin: true,
          name: 'PIN User',
          authMethod: 'pin'
        };

        const token = this.generateToken(userData);

        localStorage.setItem(AUTH_TOKEN_KEY, token);
        localStorage.setItem(AUTH_METHOD_KEY, 'pin');

        this.isAuthenticated = true;
        this.currentUser = userData;

        console.log('✅ PIN authentication successful');
        return { success: true, user: userData };
      } else {
        throw new Error('Invalid PIN');
      }

    } catch (error) {
      console.error('❌ PIN authentication failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Set PIN
  async setPin(pin) {
    try {
      const hashedPin = CryptoJS.SHA256(pin).toString();
      localStorage.setItem('user_pin', hashedPin);
      console.log('✅ PIN set successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ PIN setup failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Get current user
  getCurrentUser() {
    if (this.isLoggedIn() && this.currentUser) {
      return this.currentUser;
    }

    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));

        // Get additional user data from stored profile
        const storedProfile = localStorage.getItem('user_profile');
        let profileData = {};

        if (storedProfile) {
          try {
            profileData = JSON.parse(storedProfile);
          } catch (e) {
            console.warn('[Auth] Could not parse user profile');
          }
        }

        // Combine JWT data with stored profile data
        return {
          id: payload.id,
          email: payload.email,
          name: profileData.name || 'User',
          nationality: profileData.nationality || '',
          acceptNews: profileData.acceptNews || false,
          authMethod: this.getAuthMethod(),
          createdAt: profileData.createdAt || null
        };
      } catch {
        return null;
      }
    }
    return null;
  }

  // Logout
  logout() {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_METHOD_KEY);

    this.isAuthenticated = false;
    this.currentUser = null;
    this.authMethod = null;

    console.log('👋 User logged out');
  }

  // Check capabilities
  getAvailableAuthMethods() {
    const methods = ['password'];

    if ('NDEFReader' in window) {
      methods.push('nfc');
    }

    if (window.PublicKeyCredential) {
      methods.push('biometric');
    }

    methods.push('pin');

    return methods;
  }
}

export default new AuthManager();